import { TimeSlot } from '@wix/ambassador-bookings-availability-v2-time-slot/types';
import { CalendarState } from '../../components/BookingCalendar/controller';
import { CalendarContext } from '../context/contextFactory';
import {
  BookingPreference,
  BookingPreferenceOption,
  getBookingPreferences,
  SelectedBookingPreference,
} from './bookingPreferences';
import {
  isSlotLocked,
  isSlotWithOpenWaitingList,
} from '../slotAvailability/slotAvailability';
import { CalendarErrors, Preference } from '../../types/types';
import { DO_NOT_CARE_STAFF } from '../../constants/constants';
import {
  getServiceMaxParticipantsPerBooking,
  isBookFlowSettingsEnabled as isBookFlowSettingsEnabledUtil,
} from '@wix/bookings-calendar-catalog-viewer-mapper';
import {
  DisplayTarget,
  PreferenceType,
} from '@wix/bookings-services-preferences-modal/utils';

export const getBookingPreferencesForSelectedTime = ({
  selectableSlotsAtSelectedTime,
  selectedBookingPreferences,
  calendarErrors,
  context,
  state,
}: {
  selectableSlotsAtSelectedTime: TimeSlot[];
  selectedBookingPreferences?: SelectedBookingPreference[];
  calendarErrors: CalendarErrors[];
  context: CalendarContext;
  state: CalendarState;
}): BookingPreference[] => {
  const bookingPreferences = getBookingPreferences({
    context,
    state,
  });
  return createBookingPreferencesForSelectedTime({
    bookingPreferences,
    selectableSlotsAtSelectedTime,
    selectedBookingPreferences,
    calendarErrors,
    context,
    state,
  });
};

const createBookingPreferencesForSelectedTime = ({
  bookingPreferences,
  selectableSlotsAtSelectedTime,
  selectedBookingPreferences,
  calendarErrors,
  context,
  state,
}: {
  bookingPreferences: BookingPreference[];
  selectableSlotsAtSelectedTime: TimeSlot[];
  selectedBookingPreferences?: SelectedBookingPreference[];
  calendarErrors: CalendarErrors[];
  context: CalendarContext;
  state: CalendarState;
}): BookingPreference[] => {
  const { servicesInView } = state;
  const maxParticipantsPerBook = getServiceMaxParticipantsPerBooking(
    servicesInView[0],
  );
  const isMultipleParticipantsPerBookAllowed =
    !!maxParticipantsPerBook && maxParticipantsPerBook > 1;

  const bookingPreferencesWithOptions = bookingPreferences.map(
    (currentBookingPreference: BookingPreference) => {
      const {
        key,
        error,
        placeholder,
        isMultipleChoices,
        disabled,
        id,
        label,
      } = currentBookingPreference;
      const hasError = calendarErrors.includes(error.key);
      const options = createOptionsForBookingPreference({
        selectableSlotsAtSelectedTime,
        bookingPreferences,
        selectedBookingPreferences,
        currentBookingPreference,
        state,
        context,
      });
      const note = createNoteForBookingPreference({
        selectableSlotsAtSelectedTime,
        bookingPreferences,
        selectedBookingPreferences,
        currentBookingPreference,
        context,
        state,
      });
      const openSpotsRemained = createOpenSpotsRemainedForBookingPreference({
        selectableSlotsAtSelectedTime,
        bookingPreferences,
        selectedBookingPreferences,
        currentBookingPreference,
        state,
        context,
      });

      const isGroupBookings = createGroupBookingsForBookingPreference({
        selectableSlotsAtSelectedTime,
        currentBookingPreference,
        selectedBookingPreferences,
        bookingPreferences,
        context,
        state,
      });

      const selectedPreferenceValue = selectedBookingPreferences?.find(
        (pref) => pref.key === key,
      )?.value;

      const selectableOptions = options.filter((option) => option.isSelectable);

      const preselectedOptionId =
        selectableOptions.length === 1
          ? selectableOptions[0].id
          : selectedPreferenceValue;

      return {
        id,
        isMultipleChoices: isGroupBookings && isMultipleChoices,
        openSpotsRemained,
        preselectedOptionId,
        note,
        disabled,
        key,
        options,
        error: {
          key: error.key,
          message: hasError ? error.message : '',
        },
        placeholder,
        label,
      };
    },
  );
  if (isMultipleParticipantsPerBookAllowed) {
    const isAllPreferencesSelected = bookingPreferencesWithOptions
      .filter(({ key }) => key !== Preference.CUSTOM)
      .every(({ options, key }) => {
        return (
          options.length === 1 ||
          selectedBookingPreferences!.some(
            ({ key: selectedBookingPreferencesKey }) =>
              key === selectedBookingPreferencesKey,
          )
        );
      });
    bookingPreferencesWithOptions
      .filter(({ key }) => key === Preference.CUSTOM)
      .forEach((bookingPreference) => {
        bookingPreference.disabled = !isAllPreferencesSelected;
      });
  }
  return bookingPreferencesWithOptions;
};

const createGroupBookingsForBookingPreference = ({
  selectableSlotsAtSelectedTime,
  currentBookingPreference,
  bookingPreferences,
  selectedBookingPreferences,
  context,
  state,
}: {
  selectableSlotsAtSelectedTime: TimeSlot[];
  bookingPreferences: BookingPreference[];
  selectedBookingPreferences?: SelectedBookingPreference[];
  currentBookingPreference: BookingPreference;
  context: CalendarContext;
  state: CalendarState;
}): boolean => {
  if (currentBookingPreference.key === Preference.CUSTOM) {
    const filteredBookableSlots: TimeSlot[] =
      filterBookableSlotsAccordingToSelectedPreferences({
        selectableSlotsAtSelectedTime,
        bookingPreferences,
        selectedBookingPreferences,
        currentBookingPreference,
        context,
        state,
      });
    return !(
      isSlotWithOpenWaitingList(filteredBookableSlots[0]) ||
      isSlotLocked(filteredBookableSlots[0])
    );
  }
  return true;
};

const createOpenSpotsRemainedForBookingPreference = ({
  selectableSlotsAtSelectedTime,
  bookingPreferences,
  selectedBookingPreferences,
  currentBookingPreference,
  state,
  context,
}: {
  selectableSlotsAtSelectedTime: TimeSlot[];
  bookingPreferences: BookingPreference[];
  selectedBookingPreferences?: SelectedBookingPreference[];
  currentBookingPreference: BookingPreference;
  state: CalendarState;
  context: CalendarContext;
}): number => {
  if (currentBookingPreference.key === Preference.CUSTOM) {
    const { selectedVariantsOptions, servicesInView } = state;
    const totalParticipants = selectedVariantsOptions.reduce(
      (currentNumberOfParticipants, { numberOfParticipants }) =>
        currentNumberOfParticipants + numberOfParticipants,
      0,
    );

    const maxParticipantsPerBook = getServiceMaxParticipantsPerBooking(
      servicesInView[0],
    );

    const filteredBookableSlots: TimeSlot[] =
      filterBookableSlotsAccordingToSelectedPreferences({
        selectableSlotsAtSelectedTime,
        bookingPreferences,
        selectedBookingPreferences,
        currentBookingPreference,
        context,
        state,
      });

    return (
      Math.min(
        maxParticipantsPerBook,
        filteredBookableSlots[0].bookableCapacity!,
      ) - totalParticipants
    );
  }

  return 1;
};

const createNoteForBookingPreference = ({
  selectableSlotsAtSelectedTime,
  bookingPreferences,
  selectedBookingPreferences,
  currentBookingPreference,
  context,
  state,
}: {
  selectableSlotsAtSelectedTime: TimeSlot[];
  bookingPreferences: BookingPreference[];
  selectedBookingPreferences?: SelectedBookingPreference[];
  currentBookingPreference: BookingPreference;
  context: CalendarContext;
  state: CalendarState;
}): string => {
  if (currentBookingPreference.key === Preference.CUSTOM) {
    const { t } = context;
    const { servicesInView } = state;

    const filteredBookableSlots: TimeSlot[] =
      filterBookableSlotsAccordingToSelectedPreferences({
        selectableSlotsAtSelectedTime,
        bookingPreferences,
        selectedBookingPreferences,
        currentBookingPreference,
        context,
        state,
      });

    const maxParticipantsPerBook = getServiceMaxParticipantsPerBooking(
      servicesInView[0],
    );

    return t('app.booking-details.dropdowns.custom-preferences.note.text', {
      openSpotsRemained: Math.min(
        maxParticipantsPerBook,
        filteredBookableSlots[0].bookableCapacity!,
      ),
    });
  }

  return '';
};

const createOptionsForBookingPreference = ({
  selectableSlotsAtSelectedTime,
  bookingPreferences,
  selectedBookingPreferences,
  currentBookingPreference,
  state,
  context,
}: {
  selectableSlotsAtSelectedTime: TimeSlot[];
  bookingPreferences: BookingPreference[];
  selectedBookingPreferences?: SelectedBookingPreference[];
  currentBookingPreference: BookingPreference;
  state: CalendarState;
  context: CalendarContext;
}): BookingPreferenceOption[] => {
  const filteredBookableSlots: TimeSlot[] =
    filterBookableSlotsAccordingToSelectedPreferences({
      selectableSlotsAtSelectedTime,
      bookingPreferences,
      selectedBookingPreferences,
      currentBookingPreference,
      context,
      state,
    });
  if (currentBookingPreference.getBookingPreferenceOptionFromSlot) {
    let bookingPreferenceOptions: BookingPreferenceOption[] = [];
    selectableSlotsAtSelectedTime.forEach((bookableSlot) => {
      const slotGeneratedOptions =
        currentBookingPreference.getBookingPreferenceOptionFromSlot!(
          bookableSlot,
        );

      slotGeneratedOptions.forEach((slotOption) => {
        const isSelectable = isBookingPreferenceOptionSelectable({
          filteredBookableSlots,
          currentBookingPreference,
          slotBookingPreferenceOption: slotOption,
        });

        const isWithWaitingList = isBookingPreferenceOptionWithWaitingList({
          selectableSlotsAtSelectedTime,
          currentBookingPreference,
          slotBookingPreferenceOption: slotOption,
        });
        bookingPreferenceOptions.push({
          ...slotOption,
          isWithWaitingList,
          isSelectable,
        });
      });
    });

    bookingPreferenceOptions = removeDuplicatePreferenceOptions(
      bookingPreferenceOptions,
    );

    return bookingPreferenceOptions;
  } else if (
    currentBookingPreference.getBookingPreferenceOptionsFromSelectedVariantsOptions
  ) {
    return currentBookingPreference
      .getBookingPreferenceOptionsFromSelectedVariantsOptions(
        state.selectedVariantsOptions,
      )
      .map((option) => ({
        ...option,
        isSelectable: true,
      }));
  }
  return [];
};

const removeDuplicatePreferenceOptions = (
  bookingPreferenceOptions: BookingPreferenceOption[],
): BookingPreferenceOption[] => {
  const uniqueBookingPreferenceOptions: BookingPreferenceOption[] = [];

  bookingPreferenceOptions.forEach((bookingPreferenceOption) => {
    const uniqueBookingPreferenceOptionWithTheSameId =
      uniqueBookingPreferenceOptions.find(
        (uniqueBookingPreferenceOption: BookingPreferenceOption) =>
          bookingPreferenceOption.id === uniqueBookingPreferenceOption.id,
      );

    const isBookingPreferenceOptionNotExists =
      !uniqueBookingPreferenceOptionWithTheSameId;
    if (isBookingPreferenceOptionNotExists) {
      uniqueBookingPreferenceOptions.push(bookingPreferenceOption);
    }
  });

  return uniqueBookingPreferenceOptions;
};

const getPreferencesToFilterBy = ({
  bookingPreferences,
  context,
  state,
}: {
  bookingPreferences: BookingPreference[];
  context: CalendarContext;
  state: CalendarState;
}): BookingPreference[] => {
  const { experiments, bookingsSettings } = context;
  const { appointmentPreferencesDisplay } = state;
  const isBookFlowSettingsEnabled = isBookFlowSettingsEnabledUtil({
    experiments,
    bookingsSettings,
  });

  if (!isBookFlowSettingsEnabled || !appointmentPreferencesDisplay) {
    return bookingPreferences;
  }

  return bookingPreferences.filter((preference) => {
    switch (preference.key) {
      case Preference.STAFF_MEMBER:
        return (
          appointmentPreferencesDisplay[PreferenceType.STAFF_MEMBER] ===
          DisplayTarget.CALENDAR
        );
      case Preference.LOCATION:
        return (
          appointmentPreferencesDisplay[PreferenceType.LOCATION] ===
          DisplayTarget.CALENDAR
        );
      case Preference.DURATION:
      case Preference.CUSTOM:
        return (
          appointmentPreferencesDisplay[PreferenceType.DYNAMIC_PRICE] ===
          DisplayTarget.CALENDAR
        );
      default:
        return true;
    }
  });
};

const filterBookableSlotsAccordingToSelectedPreferences = ({
  selectableSlotsAtSelectedTime,
  bookingPreferences,
  selectedBookingPreferences,
  currentBookingPreference,
  context,
  state,
}: {
  selectableSlotsAtSelectedTime: TimeSlot[];
  bookingPreferences: BookingPreference[];
  selectedBookingPreferences?: SelectedBookingPreference[];
  currentBookingPreference: BookingPreference;
  context: CalendarContext;
  state: CalendarState;
}): TimeSlot[] => {
  let filteredBookableSlotsAtSelectedTime = selectableSlotsAtSelectedTime;

  const preferencesToFilterBy = getPreferencesToFilterBy({
    bookingPreferences,
    context,
    state,
  });

  preferencesToFilterBy.forEach((preference: BookingPreference) => {
    const selectedBookingPreference = selectedBookingPreferences?.find(
      (selectedPreference) => preference.key === selectedPreference.key,
    );

    if (
      selectedBookingPreference &&
      preference.key !== currentBookingPreference.key &&
      preference.key !== Preference.CUSTOM &&
      selectedBookingPreference.value !== DO_NOT_CARE_STAFF
    ) {
      filteredBookableSlotsAtSelectedTime =
        filteredBookableSlotsAtSelectedTime.filter((bookableSlot) =>
          preference.getBookingPreferenceOptionFromSlot!(bookableSlot).some(
            (opt) => opt.id === selectedBookingPreference.value,
          ),
        );
    }
  });
  return filteredBookableSlotsAtSelectedTime;
};

const isBookingPreferenceOptionSelectable = ({
  filteredBookableSlots,
  currentBookingPreference,
  slotBookingPreferenceOption,
}: {
  filteredBookableSlots: TimeSlot[];
  currentBookingPreference: BookingPreference;
  slotBookingPreferenceOption: BookingPreferenceOption;
}) => {
  return filteredBookableSlots.some((filteredSlot: TimeSlot) => {
    return currentBookingPreference.getBookingPreferenceOptionFromSlot!(
      filteredSlot,
    ).some((opt) => opt.id === slotBookingPreferenceOption.id);
  });
};

const isBookingPreferenceOptionWithWaitingList = ({
  selectableSlotsAtSelectedTime,
  currentBookingPreference,
  slotBookingPreferenceOption,
}: {
  selectableSlotsAtSelectedTime: TimeSlot[];
  currentBookingPreference: BookingPreference;
  slotBookingPreferenceOption: BookingPreferenceOption;
}) => {
  return selectableSlotsAtSelectedTime
    .filter((bookableSlot) => {
      return currentBookingPreference.getBookingPreferenceOptionFromSlot!(
        bookableSlot,
      ).some((opt) => opt.id === slotBookingPreferenceOption.id);
    })
    .every(isSlotWithOpenWaitingList);
};
